import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { NavBack } from '@/components/nav-back.tsx';
import { MSG } from '@/lib/constants.ts';
import { submitUserInfo } from '@/lib/user.api.ts';
import { returnUrl } from '@/lib/utils.ts';
import { signIn } from '@/store/auth.slice.ts';
import { askAlert } from '@/store/dialog.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { DaoUser } from '@/types/dao.ts';
import type { PageProps } from '@/types/props.ts';

/** 시안 안내 문구 기준. 백엔드도 같은 규칙으로 검증한다. */
const NICKNAME_RULE = /^[가-힣a-zA-Z0-9]{2,10}$/;

/**
 * 회원정보 수정 (시안 mypage-edit.html).
 *
 * 저장은 `/api/user/info.json` 을 그대로 쓴다. 그 엔드포인트는 담겨 온 항목만
 * 검증·저장하므로 닉네임과 마케팅 동의만 보내면 된다.
 *
 * 마케팅 동의를 여기서 처음 켜면 3단계 보상이 적립된다(아직 안 받았다면).
 * 금액은 `data/policy.ts` 의 `INFO_STEPS` 3단계 값이다.
 * 끄면 철회로 기록되고 동의 일시가 갱신된다.
 */
const MypageEdit: FunctionalComponent<PageProps> = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const hydrated = useAppSelector((state) => state.auth.hydrated);

  const [nickname, setNickname] = useState('');
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [saving, setSaving] = useState(false);
  /** 회원정보를 폼에 한 번만 채운다. 저장 후 응답으로 덮어써서 입력이 튀지 않도록. */
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!hydrated) return;
    if (!user?.id) route(`/login?r=${encodeURIComponent(returnUrl())}`, true);
  }, [hydrated, user?.id]);

  useEffect(() => {
    if (loaded || !user) return;
    setNickname(user.nickname ?? '');
    setAgreeMarketing(user.agreeMarketing === 'Y');
    setLoaded(true);
  }, [user, loaded]);

  const nicknameValid = NICKNAME_RULE.test(nickname);
  const dirty =
    nickname !== (user?.nickname ?? '') || agreeMarketing !== (user?.agreeMarketing === 'Y');

  async function handleSave() {
    if (saving || !user) return;

    if (!nicknameValid) {
      await dispatch(askAlert('닉네임은 한글·영문·숫자 2~10자로 입력해 주세요.'));
      return;
    }
    if (!dirty) {
      route('/mypage');
      return;
    }

    setSaving(true);
    try {
      const response = await submitUserInfo({
        userSeq: user.userSeq,
        nickname,
        agreeMarketing,
      });

      if (response.result === false) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }

      // 병합이 아니라 교체. 백엔드는 NULL 인 필드를 응답에서 빼고 보내므로
      // 병합하면 철회한 동의가 세션의 옛 값으로 되살아난다.
      await dispatch(signIn((response.data ?? user) as DaoUser)).unwrap();

      const rewarded = response.rewards?.total ?? 0;
      await dispatch(
        askAlert(
          rewarded > 0 ? `저장되었습니다.\n${rewarded}P가 적립되었습니다.` : '저장되었습니다.'
        )
      );
      route('/mypage');
    } catch {
      await dispatch(askAlert(MSG.apiFail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="page">
      <header class="title">
        <NavBack onBack={() => route('/mypage')} />
        <span class="nav-title">회원정보 수정</span>
      </header>

      <div class="section">
        <div class="contents">
          <div class="edit-form">
            <div class="edit-field">
              <label class="edit-field-label" for="nicknameInput">
                닉네임
              </label>
              <input
                id="nicknameInput"
                class="form-input form-input--nickname"
                type="text"
                placeholder="닉네임을 입력해주세요"
                maxLength={10}
                value={nickname}
                onInput={(e) => setNickname(e.currentTarget.value)}
              />
              <p
                class={nickname && !nicknameValid ? 'edit-field-hint is-error' : 'edit-field-hint'}
              >
                한글, 영문, 숫자만 사용 가능하며, 2~10자로 입력해 주세요.
              </p>
            </div>

            {/*
              이벤트 코드 — **일단 감춰 둔다.**

              검증 API 도 코드를 담을 표도 없어서 입력칸만 잠긴 채 놓여 있었다.
              쓸 수 없는 칸이 화면에 남아 있으면 사용자는 고장으로 읽는다.
              API 가 생기면 이 주석만 풀면 된다 (시안 `_info4.html` 도 같은 상태).

            <div class="edit-field">
              <label class="edit-field-label" for="eventCodeInput">
                이벤트코드
              </label>
              <input
                id="eventCodeInput"
                class="form-input form-input--nickname"
                type="text"
                placeholder="부여받은 이벤트 코드를 입력해주세요"
                maxLength={20}
                disabled
              />
              <p class="edit-field-hint">이벤트 코드 확인 기능은 준비 중입니다.</p>
            </div>
            */}

            <a
              href="/info"
              class="edit-field edit-field--row edit-link-btn"
              onClick={(e) => {
                e.preventDefault();
                // 이미 적립을 받은 상태이므로 수정 모드로 연다.
                route(`/info?mode=edit&r=${encodeURIComponent('/mypage/edit')}`);
              }}
            >
              <span class="edit-field-label">추가 정보 입력</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </a>

            <div class="edit-field edit-field--row">
              <span class="edit-field-label">마케팅 정보 수신 동의</span>
              <button
                type="button"
                class={agreeMarketing ? 'toggle-switch active' : 'toggle-switch'}
                role="switch"
                aria-checked={agreeMarketing}
                aria-label="마케팅 정보 수신 동의"
                onClick={() => setAgreeMarketing(!agreeMarketing)}
              >
                <span class="toggle-knob" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="edit-actions">
        <button
          type="button"
          class="btn-submit"
          disabled={saving || !nicknameValid}
          onClick={handleSave}
        >
          {saving ? '저장중…' : '저장하기'}
        </button>
      </div>
    </div>
  );
};

export default MypageEdit;
